import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AquasaveLogo } from "@/components/AquasaveLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AlertRulesPanel from "@/components/AlertRulesPanel";
import MaintenancePanel from "@/components/MaintenancePanel";
import DistrictsPanel from "@/components/DistrictsPanel";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowLeft, Plus, PencilSimple, Trash, FloppyDisk } from "@phosphor-icons/react";

const PROTOCOLS = ["LoRaWAN", "NB_IoT", "WM_Bus", "Modbus"];

function AllaccioForm({ initial, onSubmit, onCancel }) {
  const [f, setF] = useState(initial || {
    indirizzo: "", lat: 40.748, lng: 14.640,
    flusso_istantaneo_lh: 150, media_fatturata_lh: 150,
    meter_type: "LoRaWAN", battery_pct: 100,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "number" ? parseFloat(e.target.value) : e.target.value });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3">
      <div><Label className="text-xs uppercase tracking-wider text-slate-400">Indirizzo</Label>
        <Input required value={f.indirizzo} onChange={set("indirizzo")} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="allaccio-indirizzo" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase tracking-wider text-slate-400">Lat</Label>
          <Input type="number" step="0.0001" required value={f.lat} onChange={set("lat")} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" /></div>
        <div><Label className="text-xs uppercase tracking-wider text-slate-400">Lng</Label>
          <Input type="number" step="0.0001" required value={f.lng} onChange={set("lng")} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase tracking-wider text-slate-400">Flusso (L/h)</Label>
          <Input type="number" step="0.1" required value={f.flusso_istantaneo_lh} onChange={set("flusso_istantaneo_lh")} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" /></div>
        <div><Label className="text-xs uppercase tracking-wider text-slate-400">Media fatt. (L/h)</Label>
          <Input type="number" step="0.1" required value={f.media_fatturata_lh} onChange={set("media_fatturata_lh")} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" /></div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-400">Meter Type</Label>
        <Select value={f.meter_type} onValueChange={(v) => setF({ ...f, meter_type: v })}>
          <SelectTrigger className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#111827] border-slate-800">
            {PROTOCOLS.map((p) => <SelectItem key={p} value={p} className="font-mono text-xs">{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button variant="outline" type="button" onClick={onCancel} className="border-slate-700">Annulla</Button>
        <Button type="submit" className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE] gap-2" data-testid="allaccio-save-button">
          <FloppyDisk weight="bold" size={14} /> Salva
        </Button>
      </DialogFooter>
    </form>
  );
}

function TrattoForm({ initial, onSubmit, onCancel }) {
  const [f, setF] = useState(initial || {
    nome: "", coords: [[40.748, 14.640], [40.750, 14.645]],
    ingresso_lh: 5000, uscita_lh: 4800,
  });
  const setCoord = (i, j, v) => {
    const c = f.coords.map((r) => [...r]); c[i][j] = parseFloat(v); setF({ ...f, coords: c });
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "number" ? parseFloat(e.target.value) : e.target.value });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3">
      <div><Label className="text-xs uppercase tracking-wider text-slate-400">Nome tratto</Label>
        <Input required value={f.nome} onChange={set("nome")} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="tratto-nome" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase tracking-wider text-slate-400">Ingresso L/h</Label>
          <Input type="number" step="1" required value={f.ingresso_lh} onChange={set("ingresso_lh")} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" /></div>
        <div><Label className="text-xs uppercase tracking-wider text-slate-400">Uscita L/h</Label>
          <Input type="number" step="1" required value={f.uscita_lh} onChange={set("uscita_lh")} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" /></div>
      </div>
      <Label className="text-xs uppercase tracking-wider text-slate-400">Coordinate</Label>
      {[0, 1].map((i) => (
        <div key={i} className="grid grid-cols-2 gap-3">
          <Input type="number" step="0.0001" value={f.coords[i][0]} onChange={(e) => setCoord(i, 0, e.target.value)} placeholder={`Lat ${i}`} className="bg-[#0a0f1c] border-slate-800 font-mono" />
          <Input type="number" step="0.0001" value={f.coords[i][1]} onChange={(e) => setCoord(i, 1, e.target.value)} placeholder={`Lng ${i}`} className="bg-[#0a0f1c] border-slate-800 font-mono" />
        </div>
      ))}
      <DialogFooter>
        <Button variant="outline" type="button" onClick={onCancel} className="border-slate-700">Annulla</Button>
        <Button type="submit" className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE] gap-2" data-testid="tratto-save-button">
          <FloppyDisk weight="bold" size={14} /> Salva
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [dist, setDist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({ type: null, item: null });
  const [distForm, setDistForm] = useState({ nome: "", gestore: "", lunghezza_rete_km: 0 });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/district");
      setDist(data);
      setDistForm({ nome: data.nome, gestore: data.gestore, lunghezza_rete_km: data.lunghezza_rete_km });
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveDistretto = async () => {
    try {
      await api.put("/v1/admin/distretto", distForm);
      toast.success("Distretto aggiornato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const saveAllaccio = async (form) => {
    try {
      if (edit.item) await api.put(`/v1/admin/allacci/${edit.item.id}`, form);
      else await api.post("/v1/admin/allacci", form);
      toast.success("Allaccio salvato");
      setEdit({ type: null, item: null }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const deleteAllaccio = async (id) => {
    if (!window.confirm(`Eliminare allaccio ${id}?`)) return;
    try { await api.delete(`/v1/admin/allacci/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const saveTratto = async (form) => {
    try {
      if (edit.item) await api.put(`/v1/admin/tratti/${edit.item.id}`, form);
      else await api.post("/v1/admin/tratti", form);
      toast.success("Tratto salvato");
      setEdit({ type: null, item: null }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const deleteTratto = async (id) => {
    if (!window.confirm(`Eliminare tratto ${id}?`)) return;
    try { await api.delete(`/v1/admin/tratti/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  if (loading || !dist) return <div className="min-h-screen bg-[#070B14] grid place-items-center text-slate-500 font-mono text-sm">Caricamento...</div>;

  return (
    <div className="min-h-screen bg-[#070B14] text-slate-100">
      <header className="border-b border-slate-800 bg-[#070B14]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => nav("/")} data-testid="back-dashboard-button" className="text-slate-400 hover:text-slate-200 gap-2">
              <ArrowLeft size={14} /> Dashboard
            </Button>
            <div className="border-l border-slate-800 pl-4"><AquasaveLogo /></div>
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-[#3EB6F0]">Admin · {user?.email}</div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 py-8 space-y-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Anagrafica Rete</h1>
          <p className="text-slate-400 text-sm mt-1">Gestione distretto, allacci e condotte</p>
        </div>

        <Tabs defaultValue="distretto" className="w-full">
          <TabsList className="bg-[#111827] border border-slate-800">
            <TabsTrigger value="distretto" data-testid="tab-distretto">Distretto</TabsTrigger>
            <TabsTrigger value="distretti" data-testid="tab-distretti">Distretti</TabsTrigger>
            <TabsTrigger value="allacci" data-testid="tab-allacci">Allacci ({dist.allacci.length})</TabsTrigger>
            <TabsTrigger value="tratti" data-testid="tab-tratti">Tratti ({dist.tratti.length})</TabsTrigger>
            <TabsTrigger value="regole" data-testid="tab-regole">Regole Alert</TabsTrigger>
            <TabsTrigger value="manutenzione" data-testid="tab-manutenzione">Manutenzione</TabsTrigger>
          </TabsList>

          <TabsContent value="distretto" className="mt-6">
            <div className="bg-[#111827] border border-slate-800 rounded-lg p-6 max-w-2xl space-y-4">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Nome distretto</Label>
                <Input value={distForm.nome} onChange={(e) => setDistForm({ ...distForm, nome: e.target.value })}
                  className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="dist-nome-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Gestore</Label>
                <Input value={distForm.gestore} onChange={(e) => setDistForm({ ...distForm, gestore: e.target.value })}
                  className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="dist-gestore-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Lunghezza rete (km)</Label>
                <Input type="number" step="0.1" value={distForm.lunghezza_rete_km}
                  onChange={(e) => setDistForm({ ...distForm, lunghezza_rete_km: parseFloat(e.target.value) })}
                  className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="dist-km-input" /></div>
              <Button onClick={saveDistretto} data-testid="dist-save-button" className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE] gap-2">
                <FloppyDisk weight="bold" size={14} /> Salva distretto
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="distretti" className="mt-6">
            <DistrictsPanel />
          </TabsContent>

          <TabsContent value="allacci" className="mt-6">
            <div className="bg-[#111827] border border-slate-800 rounded-lg overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Utenze monitorate</div>
                <Dialog open={edit.type === "allaccio-new"} onOpenChange={(o) => setEdit(o ? { type: "allaccio-new", item: null } : { type: null, item: null })}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE] gap-2" data-testid="add-allaccio-button">
                      <Plus weight="bold" size={14} /> Nuovo allaccio
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
                    <DialogHeader><DialogTitle className="font-heading">Nuovo allaccio</DialogTitle></DialogHeader>
                    <AllaccioForm onSubmit={saveAllaccio} onCancel={() => setEdit({ type: null, item: null })} />
                  </DialogContent>
                </Dialog>
              </div>
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase tracking-widest">
                  <tr className="border-b border-slate-800">
                    <th className="text-left px-4 py-2 font-mono">ID</th>
                    <th className="text-left px-4 py-2 font-mono">Indirizzo</th>
                    <th className="text-left px-4 py-2 font-mono">Meter</th>
                    <th className="text-right px-4 py-2 font-mono">Flusso</th>
                    <th className="text-right px-4 py-2 font-mono">Fatt.</th>
                    <th className="text-center px-4 py-2 font-mono">Azioni</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {dist.allacci.map((a) => (
                    <tr key={a.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`admin-allaccio-${a.id}`}>
                      <td className="px-4 py-2 text-slate-300">{a.id}</td>
                      <td className="px-4 py-2 text-slate-400">{a.indirizzo}</td>
                      <td className="px-4 py-2 text-[#3EB6F0]">{a.meter_type}</td>
                      <td className="px-4 py-2 text-right text-slate-200">{a.flusso_istantaneo_lh}</td>
                      <td className="px-4 py-2 text-right text-slate-300">{a.media_fatturata_lh}</td>
                      <td className="px-4 py-2 text-center">
                        <Dialog open={edit.type === "allaccio-edit" && edit.item?.id === a.id} onOpenChange={(o) => setEdit(o ? { type: "allaccio-edit", item: a } : { type: null, item: null })}>
                          <DialogTrigger asChild>
                            <button className="text-slate-500 hover:text-[#3EB6F0] mr-3" data-testid={`edit-allaccio-${a.id}`}><PencilSimple size={14} weight="bold" /></button>
                          </DialogTrigger>
                          <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
                            <DialogHeader><DialogTitle className="font-heading">Modifica {a.id}</DialogTitle></DialogHeader>
                            <AllaccioForm initial={a} onSubmit={saveAllaccio} onCancel={() => setEdit({ type: null, item: null })} />
                          </DialogContent>
                        </Dialog>
                        <button className="text-slate-500 hover:text-[#EF4444]" onClick={() => deleteAllaccio(a.id)} data-testid={`delete-allaccio-${a.id}`}><Trash size={14} weight="bold" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="tratti" className="mt-6">
            <div className="bg-[#111827] border border-slate-800 rounded-lg overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Condotte idriche</div>
                <Dialog open={edit.type === "tratto-new"} onOpenChange={(o) => setEdit(o ? { type: "tratto-new", item: null } : { type: null, item: null })}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE] gap-2" data-testid="add-tratto-button">
                      <Plus weight="bold" size={14} /> Nuovo tratto
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
                    <DialogHeader><DialogTitle className="font-heading">Nuovo tratto</DialogTitle></DialogHeader>
                    <TrattoForm onSubmit={saveTratto} onCancel={() => setEdit({ type: null, item: null })} />
                  </DialogContent>
                </Dialog>
              </div>
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase tracking-widest">
                  <tr className="border-b border-slate-800">
                    <th className="text-left px-4 py-2 font-mono">ID</th>
                    <th className="text-left px-4 py-2 font-mono">Nome</th>
                    <th className="text-right px-4 py-2 font-mono">Ingresso</th>
                    <th className="text-right px-4 py-2 font-mono">Uscita</th>
                    <th className="text-right px-4 py-2 font-mono">Perdita</th>
                    <th className="text-center px-4 py-2 font-mono">Azioni</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {dist.tratti.map((t) => {
                    const p = t.ingresso_lh - t.uscita_lh;
                    return (
                      <tr key={t.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`admin-tratto-${t.id}`}>
                        <td className="px-4 py-2 text-slate-300">{t.id}</td>
                        <td className="px-4 py-2 text-slate-400">{t.nome}</td>
                        <td className="px-4 py-2 text-right text-slate-200">{t.ingresso_lh}</td>
                        <td className="px-4 py-2 text-right text-slate-300">{t.uscita_lh}</td>
                        <td className="px-4 py-2 text-right" style={{ color: p > 1000 ? "#EF4444" : "#10B981" }}>{p.toFixed(0)}</td>
                        <td className="px-4 py-2 text-center">
                          <Dialog open={edit.type === "tratto-edit" && edit.item?.id === t.id} onOpenChange={(o) => setEdit(o ? { type: "tratto-edit", item: t } : { type: null, item: null })}>
                            <DialogTrigger asChild>
                              <button className="text-slate-500 hover:text-[#3EB6F0] mr-3" data-testid={`edit-tratto-${t.id}`}><PencilSimple size={14} weight="bold" /></button>
                            </DialogTrigger>
                            <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
                              <DialogHeader><DialogTitle className="font-heading">Modifica {t.id}</DialogTitle></DialogHeader>
                              <TrattoForm initial={t} onSubmit={saveTratto} onCancel={() => setEdit({ type: null, item: null })} />
                            </DialogContent>
                          </Dialog>
                          <button className="text-slate-500 hover:text-[#EF4444]" onClick={() => deleteTratto(t.id)} data-testid={`delete-tratto-${t.id}`}><Trash size={14} weight="bold" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="regole" className="mt-6">
            <AlertRulesPanel />
          </TabsContent>

          <TabsContent value="manutenzione" className="mt-6">
            <MaintenancePanel />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
